import React, { useState, useEffect } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, TextField, Typography,
  IconButton, Button, CircularProgress, Alert, InputAdornment,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import LockResetIcon from '@mui/icons-material/LockReset'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useDispatch } from 'react-redux'
import { changePassword } from '../Auth/authActions'

const STRENGTH_LEVELS = [
  { label: 'Very weak', color: '#fd5068' },
  { label: 'Weak', color: '#ff9800' },
  { label: 'Fair', color: '#ffc83d' },
  { label: 'Good', color: '#42a5f5' },
  { label: 'Strong', color: '#21d07a' },
]

// Client-side heuristic only — a UX guide, not the source of truth. The
// backend's own Django validators (length, common-password list, similarity
// to username, etc.) are what actually decide if a password is accepted.
function getPasswordStrength(password) {
  if (!password) return null
  let score = 0
  if (password.length >= 8) score += 1
  if (password.length >= 12) score += 1
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1
  if (/\d/.test(password)) score += 1
  if (/[^A-Za-z0-9]/.test(password)) score += 1
  score = Math.min(score, 4)
  return { score, ...STRENGTH_LEVELS[score] }
}

function PasswordField({ label, value, onChange, show, onToggleShow, autoComplete, error, helperText, disabled }) {
  return (
    <TextField
      fullWidth
      margin="normal"
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      required
      disabled={disabled}
      autoComplete={autoComplete}
      error={error}
      helperText={helperText}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onToggleShow} edge="end" tabIndex={-1} aria-label={show ? 'Hide password' : 'Show password'}>
              {show ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  )
}

export default function ChangePasswordDialog({ open, onClose }) {
  const dispatch = useDispatch()
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showOld, setShowOld] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  // Reset to a clean slate every time the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setOldPassword('')
      setNewPassword('')
      setConfirmPassword('')
      setShowOld(false)
      setShowNew(false)
      setShowConfirm(false)
      setLoading(false)
      setError('')
      setSuccess(false)
    }
  }, [open])

  const strength = getPasswordStrength(newPassword)
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (mismatch) {
      setError('New passwords do not match.')
      return
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }

    setLoading(true)
    const result = await dispatch(changePassword(oldPassword, newPassword))
    setLoading(false)

    if (result.success) {
      setSuccess(true)
      setTimeout(onClose, 1400)
    } else {
      setError(result.error)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, overflow: 'hidden' } }}
    >
      {/* Gradient header, matching the profile hero banner's palette */}
      <Box sx={{
        background: (t) => t.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #4a1224 0%, #34123f 100%)'
          : 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
        px: 3, py: 2.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)',
        }}>
          <LockResetIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Box>
          <DialogTitle sx={{ p: 0, color: '#fff', fontWeight: 800, fontSize: '1.1rem', lineHeight: 1.3 }}>
            Change Password
          </DialogTitle>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
            Keep your account secure
          </Typography>
        </Box>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2.5 }}>
          {success ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1.5 }} />
              <Typography sx={{ fontWeight: 700 }}>Password updated</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Your new password is active now.
              </Typography>
            </Box>
          ) : (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              <PasswordField
                label="Current Password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                show={showOld}
                onToggleShow={() => setShowOld((v) => !v)}
                autoComplete="current-password"
                disabled={loading}
              />

              <PasswordField
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                show={showNew}
                onToggleShow={() => setShowNew((v) => !v)}
                autoComplete="new-password"
                disabled={loading}
              />

              {/* Strength meter — only shows once the user starts typing */}
              <Box sx={{ minHeight: 34, mt: -0.5, mb: 0.5 }}>
                {strength && (
                  <>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                      {[0, 1, 2, 3].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            flex: 1,
                            height: 4,
                            borderRadius: 2,
                            bgcolor: i <= strength.score - 1 ? strength.color : 'divider',
                            transition: 'background-color 0.2s ease',
                          }}
                        />
                      ))}
                    </Box>
                    <Typography variant="caption" sx={{ color: strength.color, fontWeight: 700 }}>
                      {strength.label}
                    </Typography>
                  </>
                )}
              </Box>

              <PasswordField
                label="Confirm New Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                show={showConfirm}
                onToggleShow={() => setShowConfirm((v) => !v)}
                autoComplete="new-password"
                disabled={loading}
                error={mismatch}
                helperText={mismatch ? 'Passwords do not match' : ' '}
              />
            </>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button onClick={onClose} disabled={loading}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !oldPassword || !newPassword || !confirmPassword}
              sx={{
                borderRadius: 10,
                background: 'linear-gradient(135deg, #FF385C 0%, #FF6B83 100%)',
                boxShadow: '0 4px 14px rgba(255,56,92,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #D4003A 0%, #FF385C 100%)' },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
            </Button>
          </DialogActions>
        )}
      </Box>
    </Dialog>
  )
}
